import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Save, Loader2, LineChart } from "lucide-react";

/**
 * Asset Allocation — port of Kinntegra-Old `client-asset-allocation`.
 * Fields lifted from the Angular template's `[(ngModel)]` bindings:
 *   allocationItem.LumpsumEquity, LumpsumDebt, SipEquity, SipDebt
 *   modificationReason
 */
export default function LeadAssetAllocationPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alloc, setAlloc] = useState({
    lumpsum_equity: "", lumpsum_debt: "",
    sip_equity: "", sip_debt: "",
    modification_reason: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lead-mgmt/${leadId}`);
        setLead(data.data);
        if (data.data?.asset_allocation) {
          setAlloc({ ...alloc, ...data.data.asset_allocation });
        }
      } catch (e) {
        toast.error("Failed to load lead: " + (e?.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [leadId]);

  const lumpsumTotal = (parseFloat(alloc.lumpsum_equity) || 0) + (parseFloat(alloc.lumpsum_debt) || 0);
  const sipTotal = (parseFloat(alloc.sip_equity) || 0) + (parseFloat(alloc.sip_debt) || 0);

  const save = async (next) => {
    if (lumpsumTotal !== 100 && lumpsumTotal !== 0) return toast.error(`Lumpsum split must total 100% (currently ${lumpsumTotal}%)`);
    if (sipTotal !== 100 && sipTotal !== 0) return toast.error(`SIP split must total 100% (currently ${sipTotal}%)`);
    setBusy(true);
    try {
      await api.post(`/lead-mgmt/${lead.id}/asset-allocation`, { asset_allocation: alloc });
      toast.success("Asset allocation saved");
      if (next) navigate(`/leads/${leadId}/mandate`);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-teal-600" size={32}/></div>;
  if (!lead) return <div className="p-10 text-center text-gray-400">Lead not found.</div>;

  const inp = "w-full h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
  const L = ({ children }) => <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">{children}</label>;

  return (
    <div className="px-4 py-6" data-testid="lead-asset-allocation-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/leads/${leadId}/account-opening`)}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 hover:bg-gray-100"
                  data-testid="alloc-back-btn"><ArrowLeft size={16}/></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Asset Allocation</h1>
            <p className="text-xs text-gray-500 mt-0.5">Set target Equity/Debt split for Lumpsum and SIP — {lead.first_name} {lead.last_name}</p>
          </div>
        </div>
      </div>

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-4">
            <LineChart size={15}/> Target Allocation (%)
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-3">Lumpsum</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <L>Equity %</L>
                  <input type="number" min="0" max="100" value={alloc.lumpsum_equity}
                         onChange={e => setAlloc({ ...alloc, lumpsum_equity: e.target.value })}
                         className={inp} data-testid="alloc-lumpsum-equity"/>
                </div>
                <div>
                  <L>Debt %</L>
                  <input type="number" min="0" max="100" value={alloc.lumpsum_debt}
                         onChange={e => setAlloc({ ...alloc, lumpsum_debt: e.target.value })}
                         className={inp} data-testid="alloc-lumpsum-debt"/>
                </div>
              </div>
              <div className={`mt-3 text-xs ${lumpsumTotal === 100 ? "text-emerald-600" : lumpsumTotal === 0 ? "text-gray-500" : "text-rose-600"}`}>
                Total: {lumpsumTotal}%
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-3">SIP</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <L>Equity %</L>
                  <input type="number" min="0" max="100" value={alloc.sip_equity}
                         onChange={e => setAlloc({ ...alloc, sip_equity: e.target.value })}
                         className={inp} data-testid="alloc-sip-equity"/>
                </div>
                <div>
                  <L>Debt %</L>
                  <input type="number" min="0" max="100" value={alloc.sip_debt}
                         onChange={e => setAlloc({ ...alloc, sip_debt: e.target.value })}
                         className={inp} data-testid="alloc-sip-debt"/>
                </div>
              </div>
              <div className={`mt-3 text-xs ${sipTotal === 100 ? "text-emerald-600" : sipTotal === 0 ? "text-gray-500" : "text-rose-600"}`}>
                Total: {sipTotal}%
              </div>
            </div>
          </div>

          <div>
            <L>Modification Reason (optional)</L>
            <textarea value={alloc.modification_reason}
                      onChange={e => setAlloc({ ...alloc, modification_reason: e.target.value })}
                      className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Reason for updating the allocation…"
                      data-testid="alloc-modification-reason"/>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t mt-6">
            <button onClick={() => save(false)} disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white text-sm px-6 py-2 hover:bg-gray-50 disabled:opacity-60"
                    data-testid="alloc-save-btn">
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save
            </button>
            <button onClick={() => save(true)} disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-semibold px-8 py-2 disabled:opacity-60"
                    data-testid="alloc-savenext-btn">
              Save &amp; Continue to Mandate
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
